import { createSelector } from '@reduxjs/toolkit';
import { ActivityWithContent } from 'gql/autogen/ActivityWithContent';
import { RootState } from 'store/store.types';
import { ResultInfo } from 'gql/autogen/ResultInfo';
import { SearchState } from 'component/page/search/store/search';
import { PageContentStatus } from 'core/type/PageContentStatus';
import { isDefaultGqlState } from 'store/api/activitiesGqlApi';
import { selectIsGlobalKeywordSearchPage } from 'store/slice/navigation';

const NoActivities: ActivityWithContent[] = [];

// Please update the 'Pick' clause to include the default fields from ResultInfo after integrating experiments.
export const EmptyResultInfo: ResultInfo & Required<Pick<ResultInfo, 'pagination' | 'totalActivities' | 'totalFilteredActivities'>> = {
    pagination: { size: 0, number: 0 },
    totalFilteredActivities: 0,
    totalActivities: 0,
    selectedActivityId: 0,
};

export const selectSearchState = (state: RootState): SearchState => state.search;
export const selectSearchStateIsLoading = createSelector(selectSearchState, (state: SearchState) => state.isLoading);
export const selectIsScrolling = createSelector(selectSearchState, (state) => state.isScrolling || false);
export const selectResultFromSearchState = createSelector(selectSearchState, (state: SearchState) => state.searchResponse?.result);
export const selectResultFromPopularActivitiesState = createSelector(selectSearchState, (state: SearchState) => state.popularActivities);
export const selectSortState = createSelector(selectSearchState, (state: SearchState) => state.sort);
export const selectFilterState = createSelector(selectSearchState, (state: SearchState) => state.filter);
export const selectMatrixFromSearchState = createSelector(selectSearchState, (state) => state.matrix);
export const selectResultInfoFromSearchState = createSelector(selectResultFromSearchState, (result) => (result?.resultInfo || EmptyResultInfo) as Required<ResultInfo>);
export const selectActivitiesFromSearchState = createSelector(selectResultFromSearchState, (result): ActivityWithContent[] => result?.activities || NoActivities);

export const selectIsEmptyActivitiesAfterFiltered = createSelector(selectResultInfoFromSearchState, selectActivitiesFromSearchState, (resultInfo, activities) => {
    return resultInfo.totalFilteredActivities > 0 && activities.length === 0;
});

export const selectTotalActivitiesFromSearchState = createSelector(selectSearchState, selectResultInfoFromSearchState, (state, resultInfo) => {
    if (state.isLoading) return 0;
    return resultInfo?.totalActivities || 0;
});

export const selectFilteredActivitiesFromSearchState = createSelector(selectSearchState, selectResultInfoFromSearchState, (state, resultInfo) => {
    if (state.isLoading) return 0;
    return resultInfo?.totalFilteredActivities || 0;
});

export const selectPageContentStatus = createSelector(
    selectSearchState,
    selectIsGlobalKeywordSearchPage,
    selectTotalActivitiesFromSearchState,
    (state, isGlobalKeywordSearchPage, totalActivities): PageContentStatus => {
        const errorMessage = (state.error?.message || '').toLowerCase();
        const isSupplyError = state.isError && state.error && errorMessage.includes('activity supply searcherror');

        if (state.isLoading || isDefaultGqlState(state)) {
            return PageContentStatus.Loading;
        }

        if (state.isError && !isSupplyError) {
            return PageContentStatus.StandardError;
        }

        if (isSupplyError || (state.isSuccess && totalActivities === 0 && !isGlobalKeywordSearchPage)) {
            return PageContentStatus.SupplyError;
        }
        return PageContentStatus.Success;
    },
);
